import { useState } from 'react';
import { Box, Typography, Paper, Button, Stack, useTheme, Chip, List, ListItemButton, Divider, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import { useNavigate } from 'react-router-dom';
import { useQuiz, useApp } from '../contexts';
import { isAnswerCorrect } from '../utils';
import type { Question } from '../types';
import QuestionPreviewDialog, { type PreviewItem } from '../components/QuestionPreviewDialog';

const TYPE_LABELS: Record<string, string> = { single: '单选', multiple: '多选', judge: '判断' };

export default function Result() {
  const navigate = useNavigate();
  const theme = useTheme();
  const { state } = useApp();
  const { session, totalCount, formattedTime, clearSession, startQuizWithQuestions, wrongAttempts } = useQuiz();
  const [previewItem, setPreviewItem] = useState<PreviewItem | null>(null);

  if (!session || session.questions.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 10 }}>
        <Typography color="text.secondary">没有答题记录</Typography>
        <Button variant="outlined" onClick={() => navigate('/home')} sx={{ mt: 2, borderRadius: 3 }}>
          返回首页
        </Button>
      </Box>
    );
  }
  const wrongQuestions = session.questions.filter(q => {
    const a = session.answers[q.id];
    return a && a.length > 0 && !isAnswerCorrect(q, a);
  });

  const correctQuestions = session.questions.filter(q => {
    const a = session.answers[q.id];
    return a && a.length > 0 && isAnswerCorrect(q, a);
  });

  // Questions the user got wrong at least once but eventually corrected (homework/half-exam)
  const retriedQuestions = correctQuestions.filter(q => wrongAttempts[q.id] > 0);

  // Questions answered correctly on first try (no wrong attempts)
  const plainCorrectQuestions = correctQuestions.filter(q => !(wrongAttempts[q.id] > 0));

  // Questions the user never answered
  const unansweredQuestions = session.questions.filter(q => {
    const a = session.answers[q.id];
    return !a || a.length === 0;
  });

  // Weighted scoring: plain correct = 1, retried = 0.5, wrong/unanswered = 0
  const weightedScore = plainCorrectQuestions.length + retriedQuestions.length * 0.5;
  const weightedAccuracy = totalCount > 0 ? Math.round((weightedScore / totalCount) * 100) : 0;
  const scoreLabel = weightedScore % 1 === 0 ? Math.round(weightedScore) : weightedScore;

  const findBankForQuestion = (q: Question) => {
    // For wrong-questions re-quiz, the session may span multiple banks
    // For custom/multi-bank sessions, try each bank
    for (const b of state.banks) {
      if (b.hqb.questions.some(bq => bq.id === q.id)) return b;
    }
    return state.banks[0];
  };

  const handlePreview = (q: Question) => {
    const bank = findBankForQuestion(q);
    if (!bank) return;
    setPreviewItem({ bank, question: q });
  };

  const handleReQuizWrong = () => {
    if (!wrongQuestions.length) return;
    startQuizWithQuestions(wrongQuestions, 'default', '错题重刷');
    navigate('/quiz/active');
  };

  const handleReQuizRetried = () => {
    if (!retriedQuestions.length) return;
    startQuizWithQuestions(retriedQuestions, 'default', '改对题重刷');
    navigate('/quiz/active');
  };

  const handleReQuizUnanswered = () => {
    if (!unansweredQuestions.length) return;
    startQuizWithQuestions(unansweredQuestions, 'default', '未答题重刷');
    navigate('/quiz/active');
  };

  const handleReQuizAll = () => {
    const all = [...wrongQuestions, ...retriedQuestions, ...unansweredQuestions];
    if (!all.length) return;
    startQuizWithQuestions(all, 'default', '全重刷');
    navigate('/quiz/active');
  };

  const handleReQuizFullSet = () => {
    if (!session.questions.length) return;
    startQuizWithQuestions([...session.questions], 'default', '重刷本套');
    navigate('/quiz/active');
  };

  return (
    <Box sx={{ p: 2, maxWidth: 720, mx: 'auto' }}>
      {/* Score circle */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: 4, p: 4, textAlign: 'center', mb: 3,
          bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider',
        }}
      >
        <Box
          sx={{
            width: 96, height: 96, borderRadius: '50%', mx: 'auto', mb: 2,
            display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative',
            background: `conic-gradient(${theme.palette.primary.main} ${weightedAccuracy}%, transparent ${weightedAccuracy}%)`,
          }}
        >
          <Box
            sx={{
              width: 78, height: 78, borderRadius: '50%',
              bgcolor: 'background.default', display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexDirection: 'column',
            }}
          >
            <Typography sx={{ fontWeight: 800, color: 'primary.main', fontSize: '1.1rem', lineHeight: 1.1 }}>
              {scoreLabel}/{totalCount}
            </Typography>
            <Typography sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.75rem' }}>
              {weightedAccuracy}%
            </Typography>
          </Box>
        </Box>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>答题完成</Typography>
        <Stack direction="row" spacing={1} sx={{ justifyContent: 'center' }}>
          <Chip label={`${totalCount} 题`} variant="outlined" size="small" />
          <Chip label={`答对 ${plainCorrectQuestions.length}`} color="success" size="small" />
          {retriedQuestions.length > 0 && <Chip label={`改对 ${retriedQuestions.length}`} color="warning" size="small" />}
          {wrongQuestions.length > 0 && <Chip label={`答错 ${wrongQuestions.length}`} color="error" size="small" />}
          {unansweredQuestions.length > 0 && <Chip label={`未答 ${unansweredQuestions.length}`} color="info" size="small" />}
        </Stack>
        {formattedTime && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>用时 {formattedTime}</Typography>
        )}
        {(wrongQuestions.length > 0 || retriedQuestions.length > 0 || unansweredQuestions.length > 0) && (
          <Stack direction="row" spacing={1} sx={{ justifyContent: 'center', mt: 1.5 }}>
            <Button
              size="small"
              variant="contained"
              startIcon={<RefreshRoundedIcon />}
              onClick={handleReQuizAll}
              sx={{ borderRadius: 3 }}
            >
              全重刷（错题+改对题+未答题）
            </Button>
          </Stack>
        )}
        <Button
          size="small"
          variant="outlined"
          startIcon={<RefreshRoundedIcon />}
          onClick={handleReQuizFullSet}
          sx={{ borderRadius: 3, mt: 0.5 }}
        >
          重刷本套（全部题目）
        </Button>
      </Paper>

      {/* Wrong answers - always expanded */}
      {wrongQuestions.length > 0 && (
        <Accordion
          defaultExpanded={true}
          disableGutters
          sx={{
            mb: 3, borderRadius: '16px !important',
            bgcolor: 'background.paper', border: '1px solid', borderColor: 'error.light',
            '&:before': { display: 'none' },
            overflow: 'hidden',
          }}
        >
          <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />} sx={{ px: 3, py: 1 }}>
            <Stack direction="row" sx={{ alignItems: 'center', gap: 1, flex: 1, mr: 1 }}>
              <ErrorOutlineRoundedIcon fontSize="small" color="error" />
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                错题回顾 ({wrongQuestions.length})
              </Typography>
            </Stack>
            <Button
              variant="outlined" size="small"
              color="error"
              startIcon={<RefreshRoundedIcon />}
              onClick={(e) => { e.stopPropagation(); handleReQuizWrong(); }}
              sx={{ borderRadius: 3, mr: 1 }}
            >
              重刷
            </Button>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 0 }}>
            <List disablePadding>
              {wrongQuestions.map((q, i) => (
                <Box key={q.id}>
                  {i > 0 && <Divider />}
                  <ListItemButton
                    onClick={() => handlePreview(q)}
                    sx={{ px: 1.5, py: 1.5, borderRadius: 0 }}
                  >
                    <Stack direction="row" sx={{ alignItems: 'center', gap: 1, width: '100%' }}>
                      <Chip
                        size="small"
                        label={TYPE_LABELS[q.type]}
                        variant="outlined"
                        sx={{ height: 22, fontSize: '0.7rem', flexShrink: 0 }}
                      />
                      <Typography variant="body2" sx={{ flex: 1, fontWeight: 500 }}>
                        {i + 1}. {q.content}
                      </Typography>
                      {wrongAttempts[q.id] > 1 && (
                        <Chip
                          label={`选错 ${wrongAttempts[q.id]} 次`}
                          size="small"
                          color="error"
                          variant="outlined"
                          sx={{ height: 18, fontSize: '0.65rem', flexShrink: 0 }}
                        />
                      )}
                    </Stack>
                  </ListItemButton>
                </Box>
              ))}
            </List>
          </AccordionDetails>
        </Accordion>
      )}

      {/* Retried questions - corrected after wrong attempts, expanded by default */}
      {retriedQuestions.length > 0 && (
        <Accordion
          defaultExpanded={true}
          disableGutters
          sx={{
            mb: 3, borderRadius: '16px !important',
            bgcolor: 'background.paper', border: '1px solid', borderColor: 'warning.light',
            '&:before': { display: 'none' },
            overflow: 'hidden',
          }}
        >
          <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />} sx={{ px: 3, py: 1 }}>
            <Stack direction="row" sx={{ alignItems: 'center', gap: 1, flex: 1, mr: 1 }}>
              <WarningAmberRoundedIcon fontSize="small" sx={{ color: 'warning.main' }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                改对题目 ({retriedQuestions.length})
              </Typography>
            </Stack>
            <Button
              variant="outlined" size="small"
              color="warning"
              startIcon={<RefreshRoundedIcon />}
              onClick={(e) => { e.stopPropagation(); handleReQuizRetried(); }}
              sx={{ borderRadius: 3, mr: 1 }}
            >
              重刷
            </Button>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 0 }}>
            <List disablePadding>
              {retriedQuestions.map((q, i) => (
                <Box key={q.id}>
                  {i > 0 && <Divider />}
                  <ListItemButton
                    onClick={() => handlePreview(q)}
                    sx={{ px: 1.5, py: 1.5, borderRadius: 0 }}
                  >
                    <Stack direction="row" sx={{ alignItems: 'center', gap: 1, width: '100%' }}>
                      <Chip
                        size="small"
                        label={TYPE_LABELS[q.type]}
                        variant="outlined"
                        sx={{ height: 22, fontSize: '0.7rem', flexShrink: 0 }}
                      />
                      <Typography variant="body2" sx={{ flex: 1, fontWeight: 500 }}>
                        {i + 1}. {q.content}
                      </Typography>
                      <Chip
                        label={`答错 ${wrongAttempts[q.id]} 次`}
                        size="small"
                        color="warning"
                        variant="outlined"
                        sx={{ height: 18, fontSize: '0.65rem', flexShrink: 0 }}
                      />
                    </Stack>
                  </ListItemButton>
                </Box>
              ))}
            </List>
          </AccordionDetails>
        </Accordion>
      )}

      {/* Unanswered questions - expanded by default */}
      {unansweredQuestions.length > 0 && (
        <Accordion
          defaultExpanded={true}
          disableGutters
          sx={{
            mb: 3, borderRadius: '16px !important',
            bgcolor: 'background.paper', border: '1px solid', borderColor: 'info.main',
            '&:before': { display: 'none' },
            overflow: 'hidden',
          }}
        >
          <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />} sx={{ px: 3, py: 1 }}>
            <Stack direction="row" sx={{ alignItems: 'center', gap: 1, flex: 1, mr: 1 }}>
              <InfoOutlinedIcon fontSize="small" color="info" />
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                未作答题目 ({unansweredQuestions.length})
              </Typography>
            </Stack>
            <Button
              variant="outlined" size="small"
              color="info"
              startIcon={<RefreshRoundedIcon />}
              onClick={(e) => { e.stopPropagation(); handleReQuizUnanswered(); }}
              sx={{ borderRadius: 3, mr: 1 }}
            >
              重刷
            </Button>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 0 }}>
            <List disablePadding>
              {unansweredQuestions.map((q, i) => (
                <Box key={q.id}>
                  {i > 0 && <Divider />}
                  <ListItemButton
                    onClick={() => handlePreview(q)}
                    sx={{ px: 1.5, py: 1.5, borderRadius: 0 }}
                  >
                    <Stack direction="row" sx={{ alignItems: 'center', gap: 1, width: '100%' }}>
                      <Chip
                        size="small"
                        label={TYPE_LABELS[q.type]}
                        variant="outlined"
                        sx={{ height: 22, fontSize: '0.7rem', flexShrink: 0 }}
                      />
                      <Typography variant="body2" sx={{ flex: 1, fontWeight: 500 }}>
                        {i + 1}. {q.content}
                      </Typography>
                    </Stack>
                  </ListItemButton>
                </Box>
              ))}
            </List>
          </AccordionDetails>
        </Accordion>
      )}

      {/* Correct answers - collapsed by default */}
      {plainCorrectQuestions.length > 0 && (
        <Accordion
          defaultExpanded={false}
          disableGutters
          sx={{
            mb: 3, borderRadius: '16px !important',
            bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider',
            '&:before': { display: 'none' },
            overflow: 'hidden',
          }}
        >
          <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />} sx={{ px: 3, py: 1 }}>
            <Stack direction="row" sx={{ alignItems: 'center', gap: 1 }}>
              <CheckCircleOutlineRoundedIcon fontSize="small" color="success" />
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                答对题目 ({plainCorrectQuestions.length})
              </Typography>
            </Stack>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 0 }}>
            <List disablePadding>
              {plainCorrectQuestions.map((q, i) => (
                <Box key={q.id}>
                  {i > 0 && <Divider />}
                  <ListItemButton
                    onClick={() => handlePreview(q)}
                    sx={{ px: 1.5, py: 1.5, borderRadius: 0 }}
                  >
                    <Stack direction="row" sx={{ alignItems: 'center', gap: 1, width: '100%' }}>
                      <Chip
                        size="small"
                        label={TYPE_LABELS[q.type]}
                        variant="outlined"
                        sx={{ height: 22, fontSize: '0.7rem', flexShrink: 0 }}
                      />
                      <Typography variant="body2" sx={{ flex: 1, fontWeight: 500 }}>
                        {i + 1}. {q.content}
                      </Typography>
                    </Stack>
                  </ListItemButton>
                </Box>
              ))}
            </List>
          </AccordionDetails>
        </Accordion>
      )}

      <Stack direction="row" spacing={1.5}>
        <Button fullWidth variant="contained" size="large" onClick={() => { clearSession(); navigate('/home'); }} sx={{ borderRadius: 3, py: 1.5 }}>
          返回首页
        </Button>
        <Button fullWidth variant="outlined" size="large" onClick={() => navigate('/settings')} sx={{ borderRadius: 3, py: 1.5 }}>
          设置
        </Button>
      </Stack>

      <QuestionPreviewDialog item={previewItem} onClose={() => setPreviewItem(null)} />
    </Box>
  );
}
